import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  Grid,
  IconButton,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ActionButtons from '../../stateless/Common/ActionButtons';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import {
  useAllItemSubCategory,
  useDeleteItemSubCategory,
  usePatchItemSubCategory,
} from '../../Query/ItemSubCategoryQuery'

import { categoryStyles } from '../../Styles/CategoryFormStyles';
import Accordian from '../../stateless/Accordian';
import { useDebounce } from '../../../utils/useDounce';
import { useChoiceParams } from '../ManageChoice/useChoiceParams';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ItemSubCategory {
  storeType: string;
  storeId: string;
  id: string;
  nameEnglish: string;
  nameArabic: string;
  categoryImage?: string;
  categoryIcon?: string;
  addedBy: string;
  status: 'blocked' | 'unblocked';
  parentCategoryId?: string;
}

type ItemSubCategoryWithSno = ItemSubCategory & {
  sno: number;
  serialNumber: number;
};

interface ItemSubCategoryListProps {
  editCategory: (category: any) => void;
  handleAddCategory: () => void;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  mode: 'add' | 'edit';
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit'>>;
  storeId?: string;
}

const ItemSubCategoryList: React.FC<ItemSubCategoryListProps> = ({
  mode,
  setMode,
}) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ItemSubCategory | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

    const { params, dispatch } = useChoiceParams();
 const { categoryId } = useParams<{ categoryId: string }>();
  const isReady = Boolean(params && Object.keys(params).length > 0);

  const memoizedParams = useMemo(() => {
    if (!isReady || !categoryId) return {};
    return {
      ...params,
      categoryId,
    };
  }, [isReady, params, categoryId]);

  const { data: manageItemSubCategory, isLoading } = useAllItemSubCategory(memoizedParams)



  const { mutate: patchItemSubCategory } = usePatchItemSubCategory();

  const { mutate: deleteItemSubCategory } = useDeleteItemSubCategory();

  const filteredRows = React.useMemo(() => {
    const storeOwners = manageItemSubCategory?.data || [];

    const filtered = storeOwners.filter((row) => {
      const matchesSearch = row.nameEnglish?.toLowerCase().includes((params.search || '').toLowerCase());
      const matchesStatus = params.statusFilter === 'All'
      const matchesAddedBy = params.addedByFilter === 'All'
      return matchesSearch && matchesStatus && matchesAddedBy;
    });

    return filtered.map((row, index) => {
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as ItemSubCategoryWithSno;

      const rowWithSno = {
        ...row,
        id: row.id,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: i18n.language === 'ar' ? row.nameArabic : row.nameEnglish,
        addedBy: row.addedBy,
        status: row.status,
      };

      return rowWithSno as ItemSubCategoryWithSno;
    });
  }, [params.search, manageItemSubCategory, params.page, params.limit, i18n.language]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };


  const handleEditCategory = (category: ItemSubCategory) => {
    window.scrollTo(0, 0);
    setSelectedCategory(category);
    setMode('edit');
    setOpen(true);
  };

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(t('itemSubCategory.messages.selectOneBlock'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      const confirmed = window.confirm(t('itemSubCategory.messages.confirmBlock'));
      if (!confirmed) return;

      patchItemSubCategory(
        {
          id: selectedRows,
          data: { status: 'blocked' },
        },
        {
          onSuccess: () => {
            setSelectedRows([]);
          },
        }
      );
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(t('itemSubCategory.messages.selectOneUnblock'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      const confirmed = window.confirm(t('itemSubCategory.messages.confirmUnblock'));
      if (!confirmed) return;

      patchItemSubCategory(
        {
          id: selectedRows,
          data: { status: 'unblocked' },
        },
        {
          onSuccess: () => {
            setSelectedRows([]);
          },
        }
      );
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(t('itemSubCategory.messages.selectOneDelete'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      const confirmed = window.confirm(t('itemSubCategory.messages.confirmDelete'));
      if (!confirmed) return;

      selectedRows.forEach((id) => {
        deleteItemSubCategory(id, {
          onSuccess: () => {
            setSelectedRows([]);
          },
        });
      });
    }
  }, [selectedRows, deleteItemSubCategory, t]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm(t('itemSubCategory.messages.confirmDelete'));
      if (!confirmed) return;

      deleteItemSubCategory(id, {
        onSuccess: () => {
          setSelectedRows([]);
        },
      });
    },
    [deleteItemSubCategory, t]
  );

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      t('itemSubCategory.messages.confirmBlockSingle')
    );
    if (!confirmed) return;

    await patchItemSubCategory(
      {
        id: [id],
        data: { status: 'blocked' },
      },
      {
        onSuccess: () => {
          setSelectedRows([]);
        },
      }
    );
  };

  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      t('itemSubCategory.messages.confirmUnblockSingle')
    );
    if (!confirmed) return;

    await patchItemSubCategory(
      {
        id: [id],
        data: { status: 'unblocked' },
      },
      {
        onSuccess: () => {
          setSelectedRows([]);
        },
      }
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'serialNumber',
      headerName: t('itemSubCategory.columns.sno'),
      disableColumnMenu: true,
      sortable: false
    },
    {
      field: 'name',
      headerName: t('itemSubCategory.columns.categoryName'),
      flex: 1,
    },
    {
      field: 'addedBy',
      headerName: t('itemSubCategory.columns.addedBy'),
      flex: 1,
    },  
    {
      field: 'status',
      headerName: t('itemSubCategory.columns.status'),
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id;
        const validId = typeof id === 'string' ? id : '';

        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={
              () =>
                isBlocked
                  ? handleUnBlockClickButton(validId) // call only when clicked
                  : handleBlockClickButton(validId) // call only when clicked
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
     {
      field: 'actions',
      headerName: t('itemSubCategory.columns.actions'),
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        return (
          <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(params.row); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('itemSubCategory.actions.edit')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); handleDeleteButton(params.row.id); }}>
                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t('itemSubCategory.actions.delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
    // delete column removed; covered by actions menu
  ];

  return (
    <>
      <Box sx={categoryStyles.ListContainer}>
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          wrap="wrap"
          sx={{ mb: 2 }}
        >
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder={t('itemSubCategory.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: '#aaa' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Grid
            container
            justifyContent="flex-end"
            spacing={1}
            alignItems="center"
          >
            <Grid>
              <ActionButtons
                onBlock={handleBlockClick}
                onUnblock={handleUnBlockClick}
                onDelete={handleDeleteClick}
                showBlock={selectedRows.length > 0 && filteredRows.some(r => selectedRows.includes(r.id) && r.status === 'unblocked')}
                showUnblock={selectedRows.length > 0 && filteredRows.some(r => selectedRows.includes(r.id) && r.status === 'blocked')}
                showDelete={selectedRows.length > 0}
              />
            </Grid>
            <Grid>
              <Accordian
                handleAddCategory={() => {
                  setOpen(true);
                  setMode('add');
                }}
                open={open}
                setOpen={setOpen}
                mode={mode}
                initialData={
                  selectedCategory
                    ? {
                      id: selectedCategory.id || '',
                      englishName: selectedCategory.nameEnglish || '',
                      arabicName: selectedCategory.nameArabic || '',
                      categoryImage: '',
                      categoryIcon: '',
                      addedBy: selectedCategory.addedBy || '',
                      storeId: selectedCategory.storeId || '',
                      storeType: selectedCategory.storeType || '',
                    }
                    : null
                }
                buttonMode="itemSubCategory"
              />
            </Grid>
          </Grid>
        </Grid>
        <Box width="100%" sx={{ overflowX: 'auto' }}>
          <CommonDataGrid
            key={`datagrid-page-${params.page}-limit-${params.limit}`}
            rows={filteredRows}
            columns={columns}
            pageSize={params.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            checkboxSelection
            selectionModel={selectedRows}
            onRowSelectionModelChange={setSelectedRows}
            loading={isLoading}
            components={{
              LoadingOverlay: () => null,
              NoRowsOverlay: () => (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  {t('itemSubCategory.messages.noData')}
                </Box>
              ),
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default ItemSubCategoryList;
